<template>
  <div>
    <h3>SMS-Verifizierung</h3>

    <div class="alert alert-dark">
      <p><strong>Wir haben Ihnen einen Verifizierungscode per SMS gesendet.</strong></p>
      <p>Bitte geben Sie den 6-stelligen Code ein, den Sie an Ihre Mobilnummer erhalten haben.</p>
    </div>

    <div class="form-row">
      <div class="form-group col-md-6">
        <label class="form-label" for="verificationCode">Verifizierungscode<span class="text-danger">*</span>:</label>
        <input
            type="text"
            v-model="verificationCode"
            class="form-control text-center"
            id="verificationCode"
            name="verificationCode"
            maxlength="6"
            placeholder="000000"
            ref="verificationInput"
            @input="formatCode"
            @keypress.enter="handleEnterKey"
            autofocus
        >
        <div class="invalid-feedback d-block" v-if="codeError">
          {{ codeError }}
        </div>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group col-md-12">
        <button class="btn btn-secondary my-3 mr-2" @click="goBack()">Zurück</button>
        <button class="btn btn-primary my-3 mr-2" @click="verifyCode()" :disabled="isVerifying">
          <span v-if="isVerifying">Wird überprüft...</span>
          <span v-else>Code bestätigen</span>
        </button>
        <button class="btn btn-link my-3" @click="resendCode()" :disabled="isResending || cooldownActive">
          <span v-if="isResending">Wird gesendet...</span>
          <span v-else-if="cooldownActive">Code erneut senden ({{ cooldownSeconds }}s)</span>
          <span v-else>Code erneut senden</span>
        </button>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="apiError" class="alert alert-danger mt-3">
      {{ apiError }}
    </div>

    <!-- Success message -->
    <div v-if="successMessage" class="alert alert-success mt-3">
      {{ successMessage }}
    </div>

  </div>
</template>

<script>
export default {
  name: 'KSRPhoneVerification',
  data() {
    return {
      verificationCode: '',
      verificationToken: '',
      appointmentData: {},
      isVerifying: false,
      isResending: false,
      apiError: '',
      codeError: '',
      successMessage: '',
      cooldownActive: false,
      cooldownSeconds: 0
    }
  },
  created() {
    // Load verification token and appointment data
    this.verificationToken = localStorage.getItem('verificationToken') || '';
    const storedAppointmentData = localStorage.getItem('appointmentData');

    if (storedAppointmentData) {
      this.appointmentData = JSON.parse(storedAppointmentData);
    }

    if (!this.verificationToken) {
      // No verification token - redirect back
      this.goBack();
    }
  },
  mounted() {
    // Focus on the input field when component is mounted
    this.focusInput();
  },
  methods: {
    focusInput() {
      // Use nextTick to ensure DOM is fully rendered
      this.$nextTick(() => {
        if (this.$refs.verificationInput) {
          this.$refs.verificationInput.focus();
        }
      });
    },
    formatCode() {
      // Only allow numbers
      this.verificationCode = this.verificationCode.replace(/[^0-9]/g, '');
      this.codeError = '';
    },
    handleEnterKey() {
      // Only trigger verification if code is complete and not already verifying
      if (this.verificationCode.length === 6 && !this.isVerifying) {
        this.verifyCode();
      }
    },
    async verifyCode() {
      this.apiError = '';
      this.codeError = '';

      // Validate code
      if (this.verificationCode.length !== 6) {
        this.codeError = 'Bitte geben Sie den 6-stelligen Code ein.';
        return;
      }

      this.isVerifying = true;

      try {
        const response = await fetch('/api/appointment/verify-code', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify({
            verification_token: this.verificationToken,
            verification_code: this.verificationCode
          })
        });

        const data = await response.json();

        if (response.ok) {

          // Clean up localStorage
          localStorage.removeItem('verificationToken');
          //localStorage.removeItem('appointmentData');

          // Emit event to the parent component on successful verification
          this.$emit('verficationStatus', { message: 'success' });

          // Redirect after 2 seconds
          /*setTimeout(() => {
            window.location.hash = '';
          }, 2000);*/

        } else {
          // Handle verification errors
          this.codeError = data.message || 'Ungültiger Code. Bitte versuchen Sie es erneut.';
          this.$emit('verficationStatus', { message: this.codeError });

          // Re-focus input after error
          this.focusInput();
        }
      } catch (error) {
        console.error('Verification Error:', error);
        this.apiError = 'Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung.';

        // Re-focus input after error
        this.focusInput();
      } finally {
        this.isVerifying = false;
      }
    },
    async resendCode() {
      this.apiError = '';
      this.isResending = true;

      try {
        const response = await fetch('/api/appointment/resend-verification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify({
            verification_token: this.verificationToken
          })
        });

        const data = await response.json();

        if (response.ok) {
          // Start cooldown
          this.startCooldown();
          this.successMessage = 'Ein neuer Code wurde gesendet.';

          // Clear the input field and focus it
          this.verificationCode = '';
          this.focusInput();

          // Clear success message after 3 seconds
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        } else {
          this.apiError = data.message || 'Fehler beim Senden des Codes.';
        }
      } catch (error) {
        console.error('Resend Error:', error);
        this.apiError = 'Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung.';
      } finally {
        this.isResending = false;
      }
    },
    startCooldown() {
      this.cooldownActive = true;
      this.cooldownSeconds = 60;

      const countdown = setInterval(() => {
        this.cooldownSeconds--;

        if (this.cooldownSeconds <= 0) {
          this.cooldownActive = false;
          clearInterval(countdown);
        }
      }, 1000);
    },
    goBack() {
      // Go back to form
      if (this.$router) {
        this.$router.go(-1);
      } else {
        window.location.hash = '#/guest-appointment';
      }
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

#verificationCode {
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
}
</style>